/**
 * Route-level owner of one gradebook's data.
 *
 * WHY THIS EXISTS. The sheet, the Assessments page and Insights were three
 * SIBLING routes, and each called `useGradebook(id)` for itself. React Router
 * unmounts one sibling before mounting the next, so hopping between the three
 * threw the data away and re-fetched it every single time — and the payload is
 * not small: the roster, every column, the categories and EVERY MARK in the
 * book, in one response. A teacher glancing at Insights and coming back paid for
 * the whole gradebook twice.
 *
 * Making them CHILDREN of this layout fixes it structurally rather than with a
 * cache. The layout stays mounted for as long as the URL is inside
 * `/gradebooks/:gradebookId`, so its state survives navigation between the
 * children, and the lifetime rules fall out for free:
 *
 *   - move between sheet / assessments / insights  -> layout stays, ONE fetch
 *   - leave the section entirely                   -> layout unmounts, dropped
 *   - open a DIFFERENT gradebook                   -> `gradebookId` changes, so
 *                                                     `useGradebook` refetches
 *                                                     and replaces it
 *
 * No manual invalidation, no module-level cache to go stale, and nothing to
 * clear on the way out — the router's own lifecycle is the cache policy.
 *
 * A SECOND BENEFIT worth stating, because it is not obvious: the three pages now
 * share ONE `mutate`. Renaming a column on the Assessments page is already
 * visible on the sheet when the teacher navigates back, with no refetch,
 * because both were reading the same object all along.
 *
 * THE TRADE. A long-lived copy can go stale — but a gradebook is single-teacher
 * data with no collaborative editing, so the only writer is the tab you are
 * looking at, and every write already patches this copy through `mutate`. If
 * sharing ever arrives, revalidate-on-focus belongs here and nowhere else.
 */

import { Outlet, useParams } from "react-router-dom";
import { ClipboardList } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { EmptyState } from "@/components/ui/EmptyState";
import { SpinnerPage } from "@/components/ui/spinner";
import { useGradebook } from "@/gradebook/api";

export default function GradebookLayout() {
  const { gradebookId } = useParams();
  const { gradebook, loading, error, refetch, mutate } = useGradebook(gradebookId);

  if (loading) return <SpinnerPage message="Loading gradebook..." />;

  if (error || !gradebook) {
    return (
      <EmptyState
        icon={ClipboardList}
        title={error && error.status !== 404 ? "Couldn't load this gradebook" : "Gradebook not found"}
        description={
          error && error.status !== 404
            ? error.message
            : "It may have been deleted, or the link is wrong."
        }
        action={<Button asChild><Link to="/gradebooks">Back to gradebooks</Link></Button>}
      />
    );
  }

  return <Outlet context={{ gradebook, refetch, mutate }} />;
}
